/**
 * PortfolioExposuresChart — full-width portfolio allocations chart.
 *
 * Renders one line per selected asset class with optional overlay (Actual
 * Traded) and strategic dotted lines, windowed by the current timeframe
 * and vintage cursor. Trace assembly lives in the shared
 * ``lib/plotly/portfolioExposuresChart`` builder so the portal-chat
 * inline chart shows the same shapes.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { Layout } from 'plotly.js';
import PlotlyChart from '@/lib/plotly/PlotlyChart';
import ChartCard from '@/components/ChartCard';
import QueryState from '@/components/QueryState';
import { ASSET_COLORS } from '@/lib/plotly/colors';
import { mergeLayout } from '@/lib/plotly/defaults';
import {
  buildExposuresTraces,
  computeExposuresYRange,
  sliceExposures,
  type ExposureAsset,
} from '@/lib/plotly/portfolioExposuresChart';
import { getWindowRange, tickConfigForTimeframe } from '@/lib/utils/timeframe';
import { COMPACT_DATE_TICKS, compactChartLayout } from '../lib/compactChart';
import { fetchExposures, mlKeys } from '../api';
import { vintageCursorAnnotation, vintageCursorShape } from '../lib/vintageCursor';
import type { UiAsset } from '../types';
import { ASSET_DISPLAY, DISPLAY_TO_ASSET } from '../types';
import type { LiveFundId, Timeframe } from '../store';

/** Display name → series key (payload uses 'Bonds'/'Inflation', not the display names). */
const DISPLAY_TO_SERIES_KEY: Record<string, string> = {
  Equities: 'Equities',
  'Fixed Income': 'Bonds',
  Commodities: 'Commodities',
  'Inflation Swaps': 'Inflation',
};

export interface PortfolioExposuresChartProps {
  selectedAssets: ReadonlySet<UiAsset>;
  vintageDate: string;
  timeframe: Timeframe;
  fundId: LiveFundId;
  volTiming: boolean;
  transactionCosts: boolean;
  constraints: boolean;
}

export default function PortfolioExposuresChart({
  selectedAssets,
  vintageDate,
  timeframe,
  fundId,
  volTiming,
  transactionCosts,
  constraints,
}: PortfolioExposuresChartProps): JSX.Element {
  // Base exposures (always tc_on, sv_on, constraints_on).
  const baseQuery = useQuery({
    queryKey: mlKeys.exposures(fundId, true, true, true),
    queryFn: () => fetchExposures(fundId, true, true, true),
    staleTime: 5 * 60 * 1000,
  });

  // Toggled exposures only when toggles differ from base.
  const isBaseConfig = volTiming && transactionCosts && constraints;
  const toggledQuery = useQuery({
    queryKey: mlKeys.exposures(fundId, volTiming, transactionCosts, constraints),
    queryFn: () => fetchExposures(fundId, volTiming, transactionCosts, constraints),
    staleTime: 5 * 60 * 1000,
    enabled: !isBaseConfig,
  });

  const isLoading = baseQuery.isLoading || (!isBaseConfig && toggledQuery.isLoading);

  const { traces, layout } = useMemo(() => {
    const primary = isBaseConfig ? baseQuery.data : (toggledQuery.data ?? baseQuery.data);
    if (!primary?.dates?.length) {
      return { traces: [], layout: {} as Partial<Layout> };
    }

    const assets: ExposureAsset[] = [...selectedAssets].map((a) => {
      const display = ASSET_DISPLAY[a];
      const key = DISPLAY_TO_SERIES_KEY[display] ?? display;
      const assetKey = DISPLAY_TO_ASSET[display];
      return {
        key,
        label: display,
        color: assetKey ? ASSET_COLORS[assetKey] : '#999',
      };
    });

    const [startStr, endStr] = getWindowRange(vintageDate, timeframe);
    const keys = assets.map((a) => a.key);
    const sliced = sliceExposures(primary, keys, startStr, endStr);

    if (!sliced.dates.length || !assets.length) {
      return { traces: [], layout: {} as Partial<Layout> };
    }

    // Overlay "Actual Traded" base config when the user has toggles off.
    const showOverlay = !volTiming || !transactionCosts;
    const overlay =
      showOverlay && baseQuery.data?.dates?.length
        ? sliceExposures(baseQuery.data, keys, startStr, endStr)
        : null;

    const strategic = primary.steady_state_strategic ?? null;
    const out = buildExposuresTraces(sliced, {
      assets,
      overlay,
      strategic,
      strategicInLegend: false,
    });

    // Title carries the live page's TC/SV toggle suffix.
    let subtitle = '';
    if (!volTiming && !transactionCosts) subtitle = '(TC off, SV off)';
    else if (!volTiming) subtitle = '(TC on, SV off)';
    else if (!transactionCosts) subtitle = '(TC off, SV on)';
    const title = subtitle ? `Portfolio Allocations ${subtitle}` : 'Portfolio Allocations';

    const endPadded = new Date(vintageDate);
    endPadded.setMonth(endPadded.getMonth() + 1);
    const { tickformat } = tickConfigForTimeframe(timeframe);
    const cursorShape = vintageCursorShape(vintageDate);
    const cursorAnnotation = vintageCursorAnnotation(vintageDate);

    const strategicLevels = strategic
      ? keys.map((k) => strategic[k]).filter((v): v is number => v != null)
      : [];
    const yRange = computeExposuresYRange(sliced, overlay, strategicLevels);
    const preset = compactChartLayout({ title });
    const lo: Partial<Layout> = mergeLayout({
      ...preset,
      xaxis: {
        ...preset.xaxis,
        type: 'date',
        range: [startStr, endPadded.toISOString().slice(0, 10)],
        tickformat,
        ...COMPACT_DATE_TICKS,
        showgrid: true,
        gridcolor: '#E0E0E0',
      },
      yaxis: {
        ...preset.yaxis,
        ticksuffix: '%',
        tickformat: '.0f',
        showgrid: true,
        gridcolor: '#E0E0E0',
        range: yRange,
      },
      shapes: cursorShape ? [cursorShape] : [],
      annotations: cursorAnnotation ? [cursorAnnotation] : [],
      hovermode: 'x unified',
    });

    return { traces: out, layout: lo };
  }, [
    baseQuery.data,
    toggledQuery.data,
    isBaseConfig,
    selectedAssets,
    vintageDate,
    timeframe,
    volTiming,
    transactionCosts,
  ]);

  const baseHasData = Boolean(baseQuery.data?.dates?.length);
  const baseFailed = baseQuery.isError || !baseHasData;
  const emptyMessage =
    !isLoading && baseFailed && fundId !== 'mars-fqm'
      ? 'Data not yet available for this mandate.'
      : 'Select assets to view';

  return (
    <ChartCard height={300}>
      <QueryState
        query={{ isLoading, isError: false, data: traces }}
        isEmpty={(t) => t.length === 0}
        loadingLabel="Loading"
        emptyLabel={emptyMessage}
      >
        {() => <PlotlyChart data={traces} layout={layout} />}
      </QueryState>
    </ChartCard>
  );
}
